import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import type { Search } from '../models';
import type { Department } from '../models/organization';
import { register403Handler } from '../services/httpErrorBus';

interface AppContextValue {
  searchBarData: Search;
  setSearchBarData: (data: Search) => void;
  enablePublishBtn: boolean;
  setEnablePublishBtn: (v: boolean) => void;
  is403Error: boolean;
  setIs403Error: (v: boolean) => void;
  gridSelectionData: { cellsCount: number; sum: number };
  setGridSelectionData: (data: { cellsCount: number; sum: number }) => void;
  selectedDepartmentInfo: Department | null;
  setSelectedDepartmentInfo: (dept: Department | null) => void;
  publishBtnClicked: number;
  triggerPublish: () => void;
  lastHandledPublishClick: number;
  setLastHandledPublishClick: (v: number) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [searchBarData, setSearchBarData] = useState<Search>({
    showWorkspace: false,
  });
  const [enablePublishBtn, setEnablePublishBtn] = useState(false);
  const [is403Error, setIs403Error] = useState(false);
  const [gridSelectionData, setGridSelectionData] = useState({
    cellsCount: 0,
    sum: 0,
  });
  const [selectedDepartmentInfo, setSelectedDepartmentInfo] =
    useState<Department | null>(null);
  const [publishBtnClicked, setPublishBtnClicked] = useState(0);
  const [lastHandledPublishClick, setLastHandledPublishClick] = useState(0);

  const triggerPublish = () => setPublishBtnClicked((c) => c + 1);

  // Wire 403 responses from the Axios interceptor to the React error page
  useEffect(() => {
    register403Handler(() => setIs403Error(true));
  }, []);

  const value = useMemo(
    () => ({
      searchBarData,
      setSearchBarData,
      enablePublishBtn,
      setEnablePublishBtn,
      is403Error,
      setIs403Error,
      gridSelectionData,
      setGridSelectionData,
      selectedDepartmentInfo,
      setSelectedDepartmentInfo,
      publishBtnClicked,
      triggerPublish,
      lastHandledPublishClick,
      setLastHandledPublishClick,
    }),
    [
      searchBarData,
      enablePublishBtn,
      is403Error,
      gridSelectionData,
      selectedDepartmentInfo,
      publishBtnClicked,
      lastHandledPublishClick,
    ]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppContextProvider');
  return ctx;
}
